import {ref, reactive, computed, watch, nextTick} from 'vue';
import {useParser} from "@/modules/reader/annotations/parser";
import {useBookInfo} from "@/modules/reader/book/bookinfo";
import config from "@/config/config.json";
import {checkToken} from "@/modules/api/auth";
import {saveLastRead} from "@/modules/localDB/lastRead"
import {useKaraokeControl} from "@/modules/reader/karaoke/karaokeControl";
import {usePlayBlockControl} from "@/modules/reader/karaoke/playBlockControl";
import {appendAccessToken} from  "@/modules/utils/pathUtils";
import {usePageResize} from "@/modules/reader/book/pageResize";


const pageState = reactive({
    currentPage:[], //note: start from zero,
    activeDoublePage:null,
    pageMode: config.reader.defaultPageMode,
    pageCount:0,
    isAllowTogglePageMode:true,
    isTogglingPageMode:false, //whether page mode is changing, since toggling page mode will trigger setCurrentPage that will check token, we just skip token checking if this flag is true
    loadStatus:[], //array setting whether the current page has been loaded, used to determine if loading layer should display or not
    isPageSizeReady:false, //tell whether the page images converted from pdf has been loaded, this is vital to set the element size after the image dimension is available
    isSinglePageOdd:false, //used to toggle the correct page from double page mode. e.g. from page [39] (zero-based) to [38,39], and toggle back to single page mode, [39] should show.
    isFirstPage:computed(()=>{
        return (pageState.currentPage[0] === 0) ?  true : false
    }),
    isLastPage:computed(()=>{
        if (pageState.pageMode === "single") {
            return pageState.currentPage[0] ===  pageState.pageCount - 1;
        } else {
            if (pageState.isRightPageAvailable) {
                return pageState.currentPage[1] === pageState.pageCount - 1;
            } else {
                return pageState.currentPage[0] ===  pageState.pageCount - 1 ;
            }
        }
    }),
    //tell whether the next page can display in the right-hand-side in a double page manner.
    //Note that in double page mode, isRightPageAvailable should always be true, except in the case that the total page number is odd and the last page has been reached.
    //In practice, use isRightPageAvailable to determine if two pages can render for double-paging,
    //and use pageMode to determine what page mode is currently switched to
    isRightPageAvailable: computed(()=> {
        return (pageState.pageMode === "double" && pageState.currentPage.length === 2);
    })
})

export function usePaging() {

    const {readData} = useParser();
    //const {removeAllMedia, removeGlossary} = useBookInfo();
    const {karaokeState} = useKaraokeControl();
    const {playBlockState} = usePlayBlockControl();
    const { pageResizeState, setBaseWidth, setBaseHeight} = usePageResize();

    const togglePageMode = async ()=> {

        console.log("@togglePageMode")

        pageState.isTogglingPageMode = true;

        if (pageState.pageMode === "single") {
            pageState.isSinglePageOdd =  (pageState.currentPage[0] % 2 === 1) ? true : false;
        }

        pageState.pageMode = (pageState.pageMode === "single") ? "double" : "single";

        if (pageState.pageMode === "single") {
            if (pageState.isSinglePageOdd) {
                await setCurrentPage(pageState.currentPage[1]);
            } else {
                await setCurrentPage(pageState.currentPage[0]);
            }
        } else {
            await setCurrentPage(pageState.currentPage[0]);
        }
        pageState.isTogglingPageMode = false;

    }

    /**
     * Page turn core method. The parameter isPageTurnByMultiPageKaraoke indicates whether the page turn
     * is triggered by the program when multi page karaoke is active. The karaoke data won't be removed
     * to facilitate the multi page karaoke function, in other cases, all karaoke data will be removed and
     * the audio will stop when page changes
     * @param targetPage
     * @param isPageTurnByMultiPageKaraoke
     * @returns {Promise<void>}
     */
    const setCurrentPage = async (targetPage, isPageTurnByMultiPageKaraoke) => {



        if (pageState.pageMode === "single") {
            pageState.currentPage = [targetPage];
        } else {

            //if double page mode, see if the target page is the "left-hand-side" one (i.e. an even number since it's zero-based)
            //If yes, current page will be [target, target+1], otherwise, it should be [target-1, targetPage].
            //For example, if targetPage is 1 (which is on "right-hand-side", currentPage will be set to [0,1].
            //If targetPage is 2 (which is on "left-hand-side", currentPage will be set to [2,3].
            if (targetPage % 2 === 0) {
                if (targetPage !== pageState.pageCount -1) {
                    pageState.currentPage = [targetPage, targetPage + 1];
                } else {
                    pageState.currentPage = [targetPage];
                }
            } else {
                pageState.currentPage = [targetPage - 1, targetPage];
            }
        }

        //karaoke data will not be kept, so that the karaoke audio can stop if page turn is made by user, no matter it's multi-page karaoke single-page karaoke.
        if (!isPageTurnByMultiPageKaraoke) {

            //note that removeAllMedia will call karaoke reset, so use it only if page turn by user
            useBookInfo().removeAllMedia();

            /*
            if (pageState.currentPage.indexOf(karaokeState.activePageNum) < 0 ||
                pageState.currentPage.indexOf(karaokeState.multiWholePageStatus.multiPageTargetPageNum) < 0
            ) {
                useKaraokeControl().reset();
            }*/

            if (pageState.currentPage.indexOf(playBlockState.activePageNum) < 0 ||
                pageState.currentPage.indexOf(playBlockState.multiWholePageStatus.multiPageTargetPageNum) < 0 ) {
                usePlayBlockControl().reset();
            }
        }

        //This block determines which two pages should load, so that page mode can be switched smoothly.
        //Since double-page mode always starts from even number, given that page number is zero-based,
        //e.g. [0,1],[2,3],[4,5]...etc. but it won't be [1,2],[3,4],[5,6]...etc.
        //So we can determine which two pages to load when targetPage is known.
        //Note that if the total page number is an odd number, when the last page is reached, it won't be able to switched to single page mode,
        //so no need to preload the previous page.
        if ( pageState.currentPage[0] % 2 === 0) {

            await loadPageImage(pageState.currentPage[0]);
            readData(pageState.currentPage[0]);
            if (pageState.currentPage[0] < pageState.pageCount -1) {
                await loadPageImage(pageState.currentPage[0]+1);
                readData(pageState.currentPage[0]+1);
            }
        } else {
            await loadPageImage(pageState.currentPage[0]);
            readData(pageState.currentPage[0]);

            await loadPageImage(pageState.currentPage[0]-1);
            readData(pageState.currentPage[0]-1);
        }

        pageState.isPageSizeReady = true;
        await saveLastRead(pageState.currentPage[0]);

        /*
        if (!pageState.isTogglingPageMode) {
            await checkToken(localStorage.accessToken);
        }*/

    }

    /**
     *
     * Listen to image load event.
     * Also set page baseWidth and baseHeight (these are raw page dimension of the original pdf file), since some annotations will use these data.
     * @param pagenum
     */
    const loadPageImage = async (pagenum)=> {

        if (pageState.loadStatus[pagenum]) {
            return true;
        }

        let img = new Image();
        const imageLoadPromise = new Promise(resolve => {
            img = new Image();
            img.onload = resolve;
            //img.src = appendAccessToken(`${bookInfoState.ebookDownloadPath}/page-image/page-${pagenum}.jpg`);
            img.src = `${useBookInfo().bookInfoState.ebookDownloadPath}/page-image/page-${pagenum}.jpg`;
        });

        await imageLoadPromise;

        if (!pageResizeState.isBaseWidthHeightReady) {
            setBaseWidth(img.width)
            setBaseHeight(img.height);
        }

        pageState.loadStatus[pagenum] = true;
        return img;
    }


    const getCurrentPage = ()=> {

        if (pageState.pageMode === "single") {
            return [pageState.currentPage[0]];
        } else {
            return pageState.currentPage;
        }
    }

    const goNextPage = () => {
        //remove glossary when current page is navigated out in single page mode
        useBookInfo().removeGlossary();
        if (pageState.pageMode === "single") {
            setCurrentPage(pageState.currentPage[0] + 1)
        } else {
            setCurrentPage(pageState.currentPage[1] + 1)
        }
    }

    const goPrevPage = () => {
        //remove glossary when current page is navigated out in single page mode
        useBookInfo().removeGlossary();
        if (pageState.pageMode === "single") {
            setCurrentPage(pageState.currentPage[0] - 1)
        } else {
            setCurrentPage(pageState.currentPage[0] -2 )
        }
    }

    const setPageCount = (pageCount) => {
        pageState.pageCount = pageCount;
    }

    const reset = ()=>{
        console.log("reset @paging.js")
        pageState.currentPage = [];
        pageState.pageMode = config.reader.defaultPageMode;
        pageState.isTogglingPageMode = false;
        pageState.pageCount = 0;
        pageState.loadStatus = [];
        pageState.isAllowTogglePageMode = true;
        pageState.isPageSizeReady = false;
        pageState.activeDoublePage = null;
        pageState.isSinglePageOdd = false;
    }

    return {
        pageState,
        readData,
        togglePageMode,
        setCurrentPage,
        getCurrentPage,
        setPageCount,
        //bookInfoState,
        //removeAllMedia,
        goNextPage,
        goPrevPage,
        reset
    }
}